/* eslint-disable react/prop-types */
// Matches the repo's existing convention for input components — see Input.jsx
// (the observer() wrapper effectively suppresses prop-types there). We don't
// wrap OtpInput in observer (no MobX state to track), so we disable the rule
// explicitly. The JSDoc above the component documents the prop contract.

import { useEffect, useRef, useCallback, useState } from 'react';
import './index.less';

const toSlots = (value, length) => Array.from({ length }, (_, i) => value[i] || '');

// A box holds at most one digit, and the browser hands an edit over together
// with it: typing 7 over 3 arrives as "37" or "73", a code inserted next to it
// as length + 1 digits. Drop the old digit, using the caret to tell which side
// it is on, so only what the user entered remains. A shorter or longer result
// means the old digit was replaced (selected, autofilled), so keep it all.
const enteredDigits = (raw, previous, caret, length) => {
  const cleaned = raw.replace(/\D/g, '');
  const keptPrevious = previous && (cleaned.length === 2 || cleaned.length === length + 1);
  if (!keptPrevious) {
    return cleaned;
  }
  return caret === raw.length ? cleaned.slice(1) : cleaned.slice(0, -1);
};

/**
 * OtpInput — N separate single-digit boxes with auto-advance.
 *
 * Used by the TD-1976 MFA flows:
 *  - Login TOTP challenge (Phase 5)
 *  - MFA setup verify (Phase 4)
 *  - Settings disable confirmation (Phase 6)
 *
 * Parent holds the joined digit string in state, but the boxes themselves live
 * here so a box the user cleared stays empty; the parent sees the digits
 * without the gap, i.e. an incomplete code. A `value` the parent sets (e.g.
 * clearing the code) replaces the boxes. A parent that ignores onChange does
 * NOT revert them, so don't use this to block or transform input.
 *
 * Behavior:
 *  - Typing a digit auto-advances focus to the next box.
 *  - Deleting or retyping a digit changes only that box.
 *  - Backspace on an empty box moves focus back (and does not consume the keystroke).
 *  - Pasting or inserting fills one digit per box, ignoring spaces and dashes;
 *    a full code always starts at the first box.
 *  - Non-digit input is silently stripped.
 *
 * Styling: see ./index.less — matches the mockup `.otp` rules (48x56 boxes,
 * 22px bold, blue focus ring, red error variant).
 *
 * @param {object} props
 * @param {number} [props.length=6]
 * @param {string} [props.value='']  Controlled digit string (length <= length)
 * @param {(value: string) => void} [props.onChange]    Fires on every change
 * @param {(value: string) => void} [props.onComplete]  Fires when every box is filled
 * @param {boolean} [props.error]     Applies the error class
 * @param {boolean} [props.disabled]  Disables all boxes
 * @param {boolean} [props.autoFocus] Focus the first box on mount
 * @param {string}  [props.name]      Used for input id prefix / autoComplete
 */
const OtpInput = ({
  length = 6,
  value = '',
  onChange = () => {},
  onComplete = () => {},
  error = false,
  disabled = false,
  autoFocus = false,
  name = 'otp'
}) => {
  const inputsRef = useRef([]);
  const [slots, setSlots] = useState(() => toSlots(value, length));

  // A value the parent sets itself (e.g. clearing the code) replaces the boxes.
  // Our own emits already match the slots, so they keep any gap in place.
  // Keyed on `value` only: re-running on slot changes would reset a fresh gap
  // before the parent's state update lands.
  useEffect(() => {
    if (value !== slots.join('') || slots.length !== length) {
      setSlots(toSlots(value, length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
    // Run once on mount only — re-focusing on every re-render fights user input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(
    (nextSlots) => {
      setSlots(nextSlots);
      const joined = nextSlots.join('');
      onChange(joined);
      if (joined.length === length) {
        onComplete(joined);
      }
    },
    [onChange, onComplete, length]
  );

  const focusBox = (idx) => {
    if (inputsRef.current[idx]) {
      inputsRef.current[idx].focus();
    }
  };

  const setDigitAt = (idx, digit) => {
    const next = slots.slice();
    next[idx] = digit;
    emit(next);
  };

  // Writes digits into consecutive boxes starting at idx, then focuses the box
  // after the last one written (or the last box if the code is full).
  const fillFrom = (idx, digitString) => {
    const next = slots.slice();
    let cursor = idx;
    for (const ch of digitString) {
      if (cursor >= length) break;
      next[cursor] = ch;
      cursor += 1;
    }
    emit(next);
    focusBox(Math.min(cursor, length - 1));
  };

  const handleChange = (idx, raw, caret) => {
    // Non-digits are stripped silently; the input itself accepts any character.
    const digits = enteredDigits(raw, slots[idx], caret, length);
    if (digits.length === 0) {
      // Deleting clears only this box; the others keep their place.
      setDigitAt(idx, '');
      return;
    }

    // A full code (e.g. a mobile suggestion injecting it into one box) starts at
    // the first box, as with paste; a partial one fills from here.
    if (digits.length > 1) {
      fillFrom(digits.length >= length ? 0 : idx, digits);
      return;
    }

    // Single digit — set and advance focus.
    setDigitAt(idx, digits);
    if (idx < length - 1) {
      focusBox(idx + 1);
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      // If the current box is empty, step back. The mockup's wireOtp only moves
      // focus; we follow the same behavior so a single Backspace doesn't clear
      // the previous digit unexpectedly.
      if (!slots[idx] && idx > 0) {
        e.preventDefault();
        focusBox(idx - 1);
      }
      return;
    }

    if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      focusBox(idx - 1);
      return;
    }

    if (e.key === 'ArrowRight' && idx < length - 1) {
      e.preventDefault();
      focusBox(idx + 1);
    }
  };

  const handlePaste = (idx, e) => {
    // Always handle the paste here: left to the browser it lands in this one
    // box, cut to maxLength, and "123 456" or a code pasted next to an existing
    // digit loses digits.
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!pasted) {
      return;
    }
    fillFrom(pasted.length >= length ? 0 : idx, pasted);
  };

  return (
    <div
      className={`otp-input${error ? ' otp-input-error' : ''}${disabled ? ' otp-input-disabled' : ''}`}
      data-testid="otp-input">
      {slots.map((digit, idx) => (
        <input
          key={`${name}-slot-${idx}`}
          ref={(el) => (inputsRef.current[idx] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          // One more than the code, so a full code inserted next to an existing
          // digit arrives whole instead of being cut (see enteredDigits).
          maxLength={length + 1}
          // autoComplete=one-time-code lets iOS / password managers offer the OTP.
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          aria-label={`${name} digit ${idx + 1} of ${length}`}
          id={`${name}-${idx}`}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e.target.value, e.target.selectionStart)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={(e) => handlePaste(idx, e)}
        />
      ))}
    </div>
  );
};

export default OtpInput;
